/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useState, useRef, useEffect } from "react";
import usePortal from "react-useportal";
// antd:
import { Form, notification, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { GetProp, UploadProps } from "antd";

// import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer;
// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import PDFviewer from "../react/PDFviewer.tsx";
import DocViewer from "../react/DocViewer.tsx";
import ModalWindow from "../react/ModalWindow.tsx";
import AppIcons from "../react/AppIcons.tsx";
import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getAppState, changeAppState, getUser, getDict, getConfig, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz } = window;

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

type Tupload = {
  accept?:string, // ".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  directory:boolean,
  listType?:"picture" | "picture-card" | "picture-circle",
  maxCount:number,
  multiple:boolean,
  withCredentials?:boolean,
  showUploadList?:boolean,
  showDownloadIcon?:boolean,
  showRemoveIcon?:boolean,
  showPreviewIcon?:boolean,
  progress?:{[key:string]:any},
  maxSize?:number,
  previewMode?:"modal" | "fullscreen",
  previewWidth?:number,
  previewHeight?:number,
  allowEditOfficeFiles?:boolean
};

/* defaultValue: {
  uid: '-1',
  name: 'image.png',
  status: 'done',
  url: 'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png',
} */

const RZupload:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZupload.displayName = "Upload";
  const dispatch = useAppDispatch();
  const cfg = useAppSelector(getConfig);
  const user = useAppSelector(getUser);
  const lng = useAppSelector(getLng);
  const dct = useAppSelector(getDict);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");
  // const theme = useAppSelector(getTheme);

  const {rootAttributes, key, fullPath, type, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {icon, iconClass, iconColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const uploadType = useMemo<Tupload>(() => {
    const typ:Tupload = {directory: false, multiple: false, maxCount: 1};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["picture", "picture-card", "picture-circle"].indexOf(tt)) { typ.listType = tt }
      if (~["preview-modal", "preview-fullscreen"].indexOf(tt)) { typ.previewMode = tt.split("-")[1] }
      else if (tt === "directory") { typ.directory = true }
      else if (tt === "multiple") { typ.multiple = true }
      else if (tt === "withCredentials") { typ.withCredentials = true }
      else if (tt === "showUploadList") { typ.showUploadList = true }
      else if (tt === "showDownloadIcon") { typ.showDownloadIcon = true }
      else if (tt === "showRemoveIcon") { typ.showRemoveIcon = true }
      else if (tt === "showPreviewIcon") { typ.showPreviewIcon = true }
      else if (tt === "progress") { typ.progress = { strokeWidth: 2, showInfo: false } }
      else if (tt === "allowEditOfficeFiles" || tt === "allowEdit") { typ.allowEditOfficeFiles = true }
    });

    if (attributes?.multiple) { typ.multiple = (attributes.multiple === "true"); if (!typ.multiple) { typ.maxCount = 1 } }
    if (attributes?.maxSize || attributes?.maxsize || attributes?.maxFileSize || attributes?.maxfilesize) { typ.maxSize = Number(attributes?.maxSize || attributes?.maxsize || attributes?.maxFileSize || attributes?.maxfilesize) }
    if (attributes?.maxCount || attributes?.maxcount) { typ.maxCount = Number(attributes.maxCount || attributes.maxcount); if (typ.maxCount > 1) { typ.multiple = true } }
    if (attributes?.listType || attributes?.listtype) { typ.listType = (attributes.listType || attributes.listtype).trim().toLowerCase() }
    if (attributes?.previewMode || attributes?.previewmode) {
      const pvm = (attributes.previewMode || attributes.previewmode).trim().toLowerCase();
      if (~["modal", "fullscreen"].indexOf(pvm)) { typ.previewMode = pvm }
      else if (~["preview-modal", "preview-fullscreen"].indexOf(pvm)) { typ.previewMode = pvm.split("-")[1] }
    }
    if (attributes?.directory) { typ.directory = (attributes.directory === "true") }
    if (attributes?.showUploadList) { typ.showUploadList = (attributes.showUploadList === "true") }
    if (attributes?.showDownloadIcon) { typ.showDownloadIcon = (attributes.showDownloadIcon === "true") }
    if (attributes?.showRemoveIcon) { typ.showRemoveIcon = (attributes.showRemoveIcon === "true") }
    if (attributes?.showPreviewIcon) { typ.showPreviewIcon = (attributes.showPreviewIcon === "true") }
    if (attributes?.withCredentials) { typ.withCredentials = (attributes.withCredentials === "true") }
    if (attributes?.accept || attributes?.allowedFileTypes) { typ.accept = (attributes.accept || attributes.allowedFileTypes).trim().toLowerCase() } // File types ".doc,.docx,.xml"
    if (attributes?.progress) {
      if (attributes.progress.startsWith("{") && attributes.progress.endsWith("}")) {
        let jsn; try { jsn = JSON.parse(attributes.progress) } catch (err) { console.log(err) } typ.progress = jsn;
      }
      else if (attributes.progress === "true") { typ.progress = { strokeWidth: 2, showInfo: false } }
    }
    if (attributes?.previewWidth || attributes?.previewwidth) { typ.previewWidth = Number(attributes.previewWidth || attributes.previewwidth) }
    if (attributes?.previewHeight || attributes?.previewheight) { typ.previewHeight = Number(attributes.previewHeight || attributes.previewheight) }

    // eslint-disable-next-line max-len
    if (elState.attributes?.allowEditOfficeFiles || attributes?.allowEditOfficeFiles || attributes?.allowEdit) { typ.allowEditOfficeFiles = (elState.attributes?.allowEditOfficeFiles === true || elState.attributes?.allowEditOfficeFiles === "true") || ((attributes.allowEditOfficeFiles || attributes.allowEdit) === "true") }
    return typ;
  }, [type, attributes, elState.attributes?.allowEditOfficeFiles]);

  const customUploadList = useMemo(() => {
    if (uploadType.showUploadList === false) { return false }
    const updLst = {
      showDownloadIcon: Boolean(uploadType.showDownloadIcon), downloadIcon: (uploadType.showDownloadIcon ? <AppIcons key="download-icon" icopath={"fa/FaDownload"} attrs={{size: 20, className: "rz-margin-3"}} /> : undefined),
      // eslint-disable-next-line max-len
      showPreviewIcon: Boolean(uploadType.showPreviewIcon || rz.isUndefinedOrNull(uploadType.showPreviewIcon)), previewIcon: (uploadType.showPreviewIcon || rz.isUndefinedOrNull(uploadType.showPreviewIcon) ? <AppIcons key="preview-icon" icopath={"fa/FaRegEye"} attrs={{size: 20, className: "rz-margin-3"}} /> : undefined),
      // eslint-disable-next-line max-len
      showRemoveIcon: Boolean(uploadType.showRemoveIcon || rz.isUndefinedOrNull(uploadType.showRemoveIcon)), removeIcon: (uploadType.showRemoveIcon || rz.isUndefinedOrNull(uploadType.showRemoveIcon) ? <AppIcons key="trash-icon" icopath={"fa/FaRegTrashAlt"} attrs={{size: 20, className: "rz-margin-3"}} /> : undefined)
    };
    return updLst;
  }, [uploadType]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState.userStyle, elState?.visible, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const rico = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (icon) { ico = (<AppIcons icopath={icon || ""} attrs={{className: iconClass || "rz-margin-right-5", color: iconColor}} />) }
    return ico;
  }, [icon, iconClass, iconColor]);

  // fileList
  const [fileList, setFileList] = useState([]);

  // It is necessary to avoid an error of Upload component: "Cannot add property 'thumbUrl' object is not extensible"
  /* const extensibleFileList = useMemo(() => {
    if (!elState?.value || !elState.value.length || !elState.value[0]) { return [] }
    if (rz.isArray(elState.value)) { return elState.value.map((ff) => (rz.objClone(ff))) }
    return [rz.objClone(elState.value)];
  }, [elState?.value]); */

  // new version of the extensibleFileList
  useEffect(() => {
    if (!elState?.value || !elState.value.length || !elState.value[0]) { setFileList([]) }
    else if (rz.isArray(elState.value)) { const vv = elState.value.map((ff) => (rz.objClone(ff))); setFileList(vv) }
    // @ts-ignore
    else if (rz.isObject(elState.value)) { const vv = rz.objClone(elState.value); setFileList([vv]) }
  }, [elState.value]);

  const upChildren = useMemo(() => {
    if (children && rz.isArray(children) && children[0]) { return children }
    if (elState.value && elState.value.length >= uploadType.maxCount) { return undefined }
    return (
      <button className="rz-txt-std" style={{ border: 0, background: "none" }} type="button">
        {rico || (<PlusOutlined />)}
        <div style={{ marginTop: 8 }}>Upload</div>
      </button>
    );
  }, [children, elState.value, rico, uploadType.maxCount]);

  // beforeUpload({file, allowedFileTypes: uploadType.accept, maxSize: uploadType.maxSize, errType: dct.w.allowedFileTypes, errSize: dct.w.maxFileSizeError})
  const beforeUpload = useCallback((file) => {
    if (fnc.config?.isDev) { console.log({action: "<Upload key='" + key + "'> -> beforeUpload()", file}) }
    if (uploadType.accept && !~uploadType.accept.indexOf("." + rz.strFileExtension(file.name)) && !~uploadType.accept.indexOf(file.type)) {
      notification.error({message: dct.w.allowedFileTypes + uploadType.accept, placement: "bottomRight"}); return false;
    }
    if (uploadType.maxSize && uploadType.maxSize < (file.size / 1024 / 1024)) {
      notification.error({message: dct.w.maxFileSizeError + uploadType.maxSize + " MB", placement: "bottomRight"}); return false;
    }
    const sameName = (elState.value || []).find((ff) => (rz.strCompare(ff.name, file.name, ".toLowerCase.trim")));
    if (sameName) { notification.error({message: dct.w.sameFileAlreadyExistInUploads, placement: "bottomRight"}); return false }
    // eslint-disable-next-line no-else-return
    else { return true }
  }, [key, dct, uploadType, elState.value]);

  const onChange = useCallback((uplProps) => {
    if (fnc.config?.isDev) console.log({action: "<Upload key='" + key + "'> -> onChange()", uplProps});
    if (uplProps.file && ~["uploading", "done"].indexOf(uplProps.file.status)) {
      if (uploadType.maxSize && (uplProps.file.size / 1024 / 1024) > uploadType.maxSize) { return }
      if (uploadType.accept && !~uploadType.accept.indexOf("." + rz.strFileExtension(uplProps.file.name)) && !~uploadType.accept.indexOf(uplProps.file.type)) { return }
    }
    // Special for Redux:
    const filesForState = uplProps.fileList.filter((ff) => ((rz.isUndefinedOrNull(ff.status) && ff.url) || (ff.status && ff.status !== "removed"))).map((ff) => ({
      ...ff,
      lastModifiedDate: (rz.isDate(ff.lastModifiedDate) ? rz.dateToString(ff.lastModifiedDate, "YYYY-MM-DD hh:mm:ss") : ff.lastModifiedDate),
      originFileObj: (ff.originFileObj ? {
        ...ff.originFileObj,
        lastModifiedDate: (rz.isDate(ff.originFileObj.lastModifiedDate) ? rz.dateToString(ff.originFileObj.lastModifiedDate, "YYYY-MM-DD hh:mm:ss") : ff.originFileObj.lastModifiedDate)
      } : undefined),
      url: ff.url || (ff.response && rz.switchCase([
        rz.isArray(ff.response) && ff.response[0], cfg.links.upload + (cfg.links.upload.endsWith("/") ? "" : "/") + (ff.response && ff.response[0] && ff.response[0].fileid),
        rz.isObject(ff.response) && ff.response.fileid, cfg.links.upload + (cfg.links.upload.endsWith("/") ? "" : "/") + (ff.response && ff.response.fileid),
        ""
      ])),
      fileid: rz.switchCase([
        rz.isArray(ff.response) && ff.response[0], (ff.response && ff.response[0] && ff.response[0].fileid),
        rz.isObject(ff.response) && ff.response.fileid, (ff.response && ff.response.fileid),
        ""
      ]),
      xhr: undefined
    }));
    // console.log({filesForState});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: filesForState}}));
    if (rootNamespace && (attributes.onUpload || attributes.onupload || attributes.onChange || attributes.onchange) && rootNamespace[attributes.onUpload || attributes.onupload || attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onUpload || attributes.onupload || attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, fileList: elState.value, file: uplProps.file, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, fileList: elState.value, file: uplProps.file, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, uploadType.maxSize, uploadType.accept, cfg.links.upload, user, lng, dct, safe]);

  const [showPreview, setPreview] = useState<boolean>(false);
  const [previewUri, setPreviewUri] = useState<string>("");
  const [previewFileData, setPreviewFileData] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const onPreview = useCallback((file) => {
    if (fnc.config?.isDev) console.log({action: "<Upload key='" + key + "'> -> onPreview()", file});

    if (rootNamespace && (attributes.onPreview || attributes.onpreview) && rootNamespace[attributes.onPreview || attributes.onpreview]) {
      const clientFNC = rootNamespace[attributes.onPreview || attributes.onpreview];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, file, fileList: elState.value, safe}).then((ret) => { if (ret?.err) { console.log(ret.err) } });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, file, fileList: elState.value, safe}); if (ret?.err) { console.log(ret.err) }
      }
    }
    else {
      console.log(file);
      // Internal preview
      setPreviewUri(file.url); setPreviewFileData(file.response?.base64); setFileName(file.name); setPreview(true);
    }
  }, [key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, elState.value, safe]);

  // eslint-disable-next-line destructuring/no-rename
  const { ref: divRef, openPortal, closePortal, isOpen, Portal } = usePortal({closeOnOutsideClick: true, closeOnEsc: true});
  const portalRef = useRef(null);

  useEffect(() => {
    if (showPreview && !isOpen) { openPortal() }
    else if (!showPreview && isOpen) { closePortal() }
  }, [elState.visible, permissions.visible, closePortal, isOpen, openPortal, showPreview]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  const curFileExt = useMemo(() => (
    rz.strFileExtension(fileName)
  ), [fileName]);

  return wrapperForm(
    <DivE key={key + "-parent-div"} uk-tooltip={elState.value && elState.value.length ? undefined : tooltip}>
      <Upload
        key={key} name={key} style={styl} className={clsu.join(" ")}
        listType={uploadType.listType}
        directory={uploadType.directory}
        accept={uploadType.accept}
        progress={uploadType.progress}
        showUploadList={customUploadList}
        withCredentials={uploadType.withCredentials}
        action={fnc.config.links.upload + (fnc.config.links.upload.endsWith("/") ? "" : "/") + rz.strMakeID(24)}
        multiple={uploadType.multiple}
        disabled={elState?.disabled || permissions.disabled === true}
        fileList={fileList}
        beforeUpload={beforeUpload}
        onChange={onChange}
        onPreview={onPreview}
        // onDrop={onDrop}
        // onRemove={onRemove}
        // onDownload={onDownload}
      >
        {upChildren}
      </Upload>
      <div key={key + "-dummy-div"} ref={divRef} />
      {isOpen && uploadType.previewMode && (
        <Portal key={key + "-portal"} ref={portalRef} style={{height: "100vh", width: "100vw"}} >
          <ModalWindow modalKey={key}
            isOpen={Boolean(showPreview)} onRequestClose={() => { setPreview(false); setPreviewUri(""); setFileName("") }}
            initWidth={uploadType.previewWidth || 300} initHeight={uploadType.previewHeight || 400} minWidth={200} minHeight={200}
            title={dct.p.preview} icon={rico} dct={dct} closeOnEsc={true} hasResizeable={true} hasMoveable={true}
            isMaximize={uploadType.previewMode === "fullscreen"} hasMaximize={uploadType.previewMode !== "fullscreen"}
            className="uk-flex uk-flex-center uk-flex-middle rz-theme-d3"
          >
            {/*
              curFileExt === "pdf"
              ? <PDFviewer url={previewUri} />
              : <div className="rz-width-1-1 uk-flex uk-flex-center uk-flex-middle rz-theme-d3">{fileName}</div>
            */}
            {/* <div className="rz-width-1-1 uk-flex uk-flex-center uk-flex-middle rz-theme-d3">TEST PREVIEW</div> */}
            {<DocViewer viewerKey={"universal-document-viewer-" + key}
              document={{uri: previewUri, fileType: curFileExt, fileName, allowEditOfficeFiles: uploadType.allowEditOfficeFiles}}
              className="rz-width-1-1 rz-theme-d3" style={{height: "100%"}}
            />}
          </ModalWindow>
        </Portal>
      )}
    </DivE>
  );
}

export default RZupload;
/* eslint-enable @typescript-eslint/no-unused-vars */
